/**
 * canvas-engine/renderer/elements/CardElement.jsx
 *
 * OWN per-type element renderer for `card` elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner card, filling
 * its positioned box. The parent CanvasRenderer owns positioning. DOM output is
 * identical in builder and reader modes. (Req 4.1, 16.1)
 *
 * Image resolved via the OWN `assetRef`. Pure / presentational.
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';

function str(v) {
  return v == null ? '' : String(v);
}

function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render a content card (image + title + description).
 *
 * @param {object} props
 * @param {object} props.element normalized `card` Canvas_Element
 */
export default function CardElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};

  const title = str(content.title);
  const description = str(content.description);
  const asset = assetRef(content.image);
  const imageSrc = asset.displayLink || asset.storedLink || '';
  const backgroundAsset = assetRef(style.backgroundImage);
  const backgroundImage = backgroundAsset.displayLink || backgroundAsset.storedLink || '';

  const radius = num(style.radius, num(style.borderRadius, 18));

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: color(style.backgroundColor, color(style.background, '#ffffff')),
    border: `1px solid ${color(style.borderColor, '#e2e8f0')}`,
    borderRadius: radius,
    boxShadow: typeof style.shadow === 'string' && style.shadow !== 'none' ? style.shadow : 'none',
  };
  if (backgroundImage) {
    rootStyle.backgroundImage = `url(${backgroundImage})`;
    rootStyle.backgroundSize = 'cover';
    rootStyle.backgroundPosition = 'center';
    rootStyle.backgroundRepeat = 'no-repeat';
  }

  const bodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: typeof style.padding === 'string' ? style.padding : '24px',
  };

  return (
    <div className="ce-card" style={rootStyle}>
      {imageSrc ? (
        <div className="ce-card__media" style={{ width: '100%', flex: '0 0 auto' }}>
          <img
            src={imageSrc}
            alt={title}
            style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
          />
        </div>
      ) : null}

      <div className="ce-card__body" style={bodyStyle}>
        {title ? (
          <span
            className="ce-card__title"
            style={{ color: color(style.titleColor, color(style.textColor, '#0f172a')), fontSize: 18, fontWeight: 700 }}
          >
            {title}
          </span>
        ) : null}
        {description ? (
          <span
            className="ce-card__description"
            style={{ color: color(style.descriptionColor, color(style.textColor, '#475569')), fontSize: 14, lineHeight: 1.5 }}
          >
            {description}
          </span>
        ) : null}
      </div>
    </div>
  );
}
